import { styled, useTheme } from '@mui/material/styles'
import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from 'react'
import { Marker as MarkerComponent, type MarkerProps } from 'react-map-gl/mapbox'
import { Box } from '../../components/Box.tsx'
import { Collapse, Paper, Typography } from '../../components/generic.tsx'
import { Icon, type IconName } from '../../components/Icon.tsx'
import { Stack } from '../../components/Stack.tsx'
import { useLightningAlerts } from '../../data/alerts/lightning.ts'
import type { LightningAlert } from '../../data/alerts/types.ts'
import { useAssetAlertState } from '../../data/alerts/useAlerts.ts'
import { useDeviceCombinedStatuses } from '../../data/devices.ts'
import { isAssetLocation, isDeviceLocation, type Location } from '../../data/locations.tsx'
import { useMeasurementAlertsRecord } from '../../data/measurementAlerts.ts'
import { useGetProtectionsByAsset } from '../../data/protections/useProtections.ts'
import { type Dayjs, dayjs } from '../../dayjs.ts'
import { ids } from '../../ids.ts'
import { useIsAssetAudible } from '../alerts/audibleEvents.ts'
import { UnitLabel } from '../measurements/components/MeasurementLabel.tsx'
import { useConvertMeasurement } from '../measurements/convertMeasurements.tsx'
import { useMeasurementsTableData } from '../measurements/table/useMeasurementsTableData.tsx'
import { useTimeline } from '../timeline/timelineState.ts'
import { AlertStack } from './alerts/AlertStack.tsx'
import { Circle, CountdownCircle } from './alerts/components/CountdownCircle.tsx'
import { NewAlertSpinner } from './alerts/NewAlertSpinner.tsx'

interface MapMarkerProps extends MarkerProps {
	id: string
	icon: IconName
	name: string
	longitude: number
	latitude: number
	location?: Location
	children?: ReactNode
	selected?: boolean
	collapseProps?: ComponentProps<typeof Collapse>
	interactive?: boolean
}

export const LocationMarker = ({
	id,
	icon,
	name,
	longitude,
	latitude,
	location,
	children,
	selected,
	collapseProps,
	interactive = true,
	...props
}: MapMarkerProps) => {
	const isNowLocked = useTimeline((s) => s.nowLocked)
	const protections = useGetProtectionsByAsset(location && isAssetLocation(location) ? location.assetVrn : '')
	const airTemperatureMeasurement = useDeviceAirTemperature(location && isDeviceLocation(location) ? location.id : undefined)
	const [innerExpanded, setInnerExpanded] = useState(true)

	const ref = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const updateExpanded = () => setInnerExpanded(!!ref.current?.matches(':hover'))
		document.addEventListener('mousemove', updateExpanded)
		return () => document.removeEventListener('mousemove', updateExpanded)
	}, [])

	const isExpanded = selected || innerExpanded

	return (
		<StyledMarker
			anchor='left'
			offset={[-12, 0]}
			longitude={longitude}
			latitude={latitude}
			sx={{ zIndex: isExpanded ? 1 : 0, ':hover': { zIndex: 2 } }}
			{...props}
			onClick={
				interactive
					? (e) => {
							e.originalEvent.stopPropagation()
							props.onClick?.(e)
						}
					: undefined
			}
		>
			<Paper
				ref={ref}
				data-ta={ids.map.mapMarker.marker(id)}
				component={Box}
				flex
				align='center'
				sx={{
					borderRadius: 4,
					cursor: interactive ? 'pointer' : 'initial',
					p: isExpanded ? 0.5 : 0,
					ml: isExpanded ? -0.5 : 0,
					boxShadow: isExpanded ? 2 : 0,
					opacity: isExpanded ? 1 : 0,
					transition: 'padding 0.1s, margin 0.1s, boxShadow 0.1s, opacity 0.2s',
					'@keyframes fadeIn': {
						from: { p: 0, ml: 0, boxShadow: 0, opacity: 0 },
						to: { p: 0.5, ml: -0.5, boxShadow: 2, opacity: 1 },
					},
					animation: 'fadeIn 0.1s linear',
				}}
			>
				<MarkerIcon icon={icon} location={location} />
				<Collapse in={isExpanded} appear orientation='horizontal' timeout={120} easing='easeOut' {...collapseProps}>
					<Box flex gap={1} align='center' px={1}>
						{protections && <NewAlertSpinner protections={protections} />}
						<Typography variant='body2' whiteSpace='nowrap'>
							{name}
						</Typography>
						{airTemperatureMeasurement && (
							<Box
								flex
								gap={0.5}
								sx={{ color: airTemperatureMeasurement.quality.qualityValue < 0 ? 'text.secondary' : undefined }}
								position='relative'
							>
								{airTemperatureMeasurement.quality.qualityValue < 0 && <QualityWarningIcon />}
								<Typography variant='body2' whiteSpace='nowrap'>
									{airTemperatureMeasurement.value}
								</Typography>
								<UnitLabel measurementType={airTemperatureMeasurement.measurementType} sx={{ fontSize: '12px' }} />
							</Box>
						)}
						{isNowLocked && location && <AlertStack data-ta={ids.map.mapMarker.markerAlerts} location={location} />}
					</Box>
				</Collapse>
			</Paper>
		</StyledMarker>
	)
}

const MarkerIcon = ({ icon, location }: { icon: IconName; location?: Location }) => {
	const theme = useTheme()
	const date = useTimeline((s) => s.date)

	const assetVrn = location && isAssetLocation(location) ? location.assetVrn : ''
	const deviceId = location && isDeviceLocation(location) ? location.id : undefined

	const deviceStatuses = useDeviceCombinedStatuses()
	const deviceStatus = (location && isDeviceLocation(location) ? location.status : undefined) ?? deviceStatuses[deviceId ?? '']?.status
	const badDeviceStatus = deviceStatus === 'ERROR' || deviceStatus === 'OFFLINE'

	const assetState = useAssetAlertState(assetVrn)
	const { data: lightningAlerts = [] } = useLightningAlerts()
	const activeProt = assetState?.protection
	const severity = assetState?.severity
	const percentElapsed = getPercentRemaining(
		lightningAlerts.find((a) => a.protectionVrn === activeProt?.vrn),
		date,
	)

	const measurementAlertsRecord = useMeasurementAlertsRecord()
	const measurementAlerts = measurementAlertsRecord[deviceId ?? '']

	const isAudiblyAlerting = useIsAssetAudible(assetVrn)

	const hasActiveMeasurementAlerts = (measurementAlerts?.active.length ?? 0) > 0
	const activeProtColor = severity ? theme.alerts.color[severity] : undefined
	const color = hasActiveMeasurementAlerts ? theme.alerts.color.alarm : activeProtColor
	const locationIcon = <Icon name={isAudiblyAlerting ? 'VolumeSolid' : icon} sx={{ height: '14px' }} />

	return (
		<Stack
			center
			sx={{
				height: 24,
				width: 24,
				borderRadius: '50%',
				border: '1px solid',
				bgcolor: color ? 'transparent' : 'mapMarker.bg',
				color: 'mapMarker.fg',
				borderColor: 'mapMarker.fg',
				'&:hover': { bgcolor: color ? 'transparent' : 'mapMarker.bg' },
			}}
		>
			{badDeviceStatus ? (
				<Circle color={theme.palette.devices.badStatus}>{locationIcon}</Circle>
			) : color ? (
				<CountdownCircle color={color} ringColor={color} percentElapsed={percentElapsed}>
					{locationIcon}
				</CountdownCircle>
			) : (
				locationIcon
			)}
		</Stack>
	)
}

const StyledMarker = styled(MarkerComponent, { name: 'MapMarker', slot: 'Root' })()

const getPercentRemaining = (alert: LightningAlert | undefined, date: Dayjs) => {
	if (!alert) return 100
	const startTime = dayjs(alert.startTime)
	const expectedEndTime = dayjs(alert.eventTime)
	const currentTime = date
	const totalDuration = expectedEndTime.diff(startTime)
	const elapsedDuration = currentTime.diff(startTime)
	const remainingDuration = totalDuration - elapsedDuration
	return Math.max(0, (remainingDuration / totalDuration) * 100)
}

const useDeviceAirTemperature = (deviceId: string | undefined) => {
	const { columnIds, rows } = useMeasurementsTableData()
	const convertMeasurement = useConvertMeasurement()
	if (!deviceId) return undefined
	const deviceMeasurements = rows.find((row) => row.id === deviceId)?.measurements
	if (!deviceMeasurements) return undefined
	const airTemperatureIds = columnIds.filter((id) => id.startsWith('AIR_TEMPERATURE'))
	if (airTemperatureIds.length === 0) return undefined

	for (const id of airTemperatureIds) {
		const measurement = deviceMeasurements[id]?.at(0)
		if (measurement) {
			const [convertedValue, convertedMeasurementType] = convertMeasurement(measurement.value, measurement?.measurementType)
			return { ...measurement, value: convertedValue?.toFixed(2), measurementType: convertedMeasurementType }
		}
	}

	return undefined
}

const QualityWarningIcon = () => (
	<Box
		sx={{
			position: 'absolute',
			top: 0,
			right: -3,
			border: '3px solid',
			borderTopColor: (t) => t.palette.locationQualityWarning,
			borderRightColor: (t) => t.palette.locationQualityWarning,
			borderLeftColor: 'transparent',
			borderBottomColor: 'transparent',
		}}
	/>
)
